// src/components/MetaTags.tsx
import React from "react";
import { Helmet } from "react-helmet";
import { Chapter } from "../types/Chapter";
import { devUrl, prodUrl, siteName } from "../constants/BaseConstants";
import { currentDeployment } from "../constants-dynamic/CurrentDeployment";
import {
  ChapterDescriptionClauses,
  UniversalTail,
  HomeDescription,
  HomeTitle,
  buildTitle,
  clampSnippet,
  BoosterTail,
} from "../constants/SEOMetadata";

interface MetaTagsProps {
  chapter: Chapter;
}

/** Fallback: build a short, neutral snippet from early verses (~130 chars). */
function verseSnippetFallback(chapter: Chapter): string {
  const startIndex = chapter.verses.findIndex(v => v.verseNumber >= 1);
  let s = "";
  let i = startIndex >= 0 ? startIndex : 0;
  while (s.length < 130 && i < chapter.verses.length) {
    const v = chapter.verses[i];
    const label = v.verseNumber > 0 ? `${v.verseNumber}. ` : "";
    s += `${label}${v.verseString} `;
    i++;
  }
  return s.trim().replace(/"/g, "").replace(/[“”]/g, "").replace(/—/g, ", ").replace(/’/g, "'");
}

function MetaTagsImpl({ chapter }: MetaTagsProps): JSX.Element {
  const {
    chapterNumber,
    nameTrans,
    nameEnglish, // used in title
  } = chapter;

  const { baseUrl, pageUrl, iconUrl, infographicUrl, isHome, title, description, ldJson } = React.useMemo(() => {
    const isHome = chapterNumber === 1;

    // URLs & assets
    const baseUrl = currentDeployment === "dev" ? devUrl : prodUrl;
    const pageUrl = isHome ? `${baseUrl}/` : `${baseUrl}/${chapterNumber}/`;
    const iconUrl = `${baseUrl}/images/icon.png`;
    const infographicUrl = `${baseUrl}/images/infographic.png`;

    // Title
    const title = isHome ? HomeTitle : buildTitle(nameTrans, chapterNumber, nameEnglish);

    // Description (avoid shadowing; produce once)
    let description: string;
    if (isHome) {
      description = HomeDescription;
    } else {
      const clause = ChapterDescriptionClauses[chapterNumber] ?? verseSnippetFallback(chapter);
      const base = `${clause} ${UniversalTail}`.trim();
      const needsBoost = base.length < 140;
      const rawDescription = needsBoost ? `${base}${BoosterTail}` : base;
      description = clampSnippet(rawDescription, 155);
    }

    // JSON-LD (WebSite for home, WebPage for surah pages)
    const ld = {
      "@context": "https://schema.org",
      "@type": isHome ? "WebSite" : "WebPage",
      url: pageUrl,
      name: title,
      description,
      author: "Talal Itani",
      inLanguage: "en",
      logo: iconUrl,
      image: iconUrl,
      publisher: {
        "@type": "Organization",
        name: siteName,
        logo: iconUrl,
      },
    };

    return {
      baseUrl,
      pageUrl,
      iconUrl,
      infographicUrl,
      isHome,
      title,
      description,
      ldJson: JSON.stringify(ld),
    };
  }, [chapter, chapterNumber, nameEnglish, nameTrans]);

  return (
    <Helmet>
      {/* JSON-LD: cleaner than Helmet's script prop with innerHTML casts */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: ldJson }} />

      <html lang="en" />
      <link rel="canonical" href={pageUrl} />

      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="author" content="Talal Itani" />
      <meta name="robots" content="index, follow" />

      <meta property="og:url" content={pageUrl} />
      <meta property="og:type" content={isHome ? "website" : "article"} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={iconUrl} />
      <meta property="og:site_name" content={siteName} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@ClearQuran" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:creator" content="@ClearQuran" />
      <meta name="twitter:image" content={infographicUrl} />
    </Helmet>
  );
}

// Only re-render when chapter bits that change tags actually change
export default React.memo(MetaTagsImpl, (a, b) => {
  const ca = a.chapter;
  const cb = b.chapter;

  if (ca.chapterNumber !== cb.chapterNumber) return false;
  if (ca.nameTrans !== cb.nameTrans) return false;
  if (ca.nameEnglish !== cb.nameEnglish) return false;

  // Description may fall back to verse text; if verses array identity changes,
  // we conservatively re-render (usually chapter change anyway).
  if (ca.verses !== cb.verses) return false;

  return true;
});
